import {
  Box,
  Button,
  Switch,
  Tooltip,
  Typography,
  TypographyProps,
  styled,
} from '@mui/material'
import { TooltipProps, tooltipClasses } from '@mui/material/Tooltip'
import { EvCapabilitycomplianceType } from 'interfaces'

export const StyledDashboardTitleWrapper = styled(Box)(() => ({
  width: '100%',
  paddingBlock: '20px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  textAlign: 'center',
  paddingInline: 0,
  justifyContent: 'center',
  gap: 10,
  '@media (min-width: 1200px)': {
    paddingInline: '24px',
    justifyContent: 'space-between',
    gap: 16,
  },
}))

export const StyledDashboardWrapper = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 10,
  '@media (min-width: 1200px)': {
    gap: 16,
    flexDirection: 'row',
    justifyContent: 'space-between',
    width: '100%',
  },
}))

export const StyledDashboardTitle = styled(Typography)(({ theme: { palette } }) => ({
  color: palette.grey[800],
  fontWeight: 'bold',
  fontSize: 18,
  letterSpacing: '.15rem',
  '@media (min-width: 1200px)': {
    fontWeight: 'normal',
    fontSize: 24,
    textAlign: 'left',
  },
}))

export const StyledDashboardSubTitleeWrapper = styled(Box)(() => ({
  display: 'flex',
  gap: '10px 20px',
  flexWrap: 'wrap',
  justifyContent: 'center',
  '@media (min-width: 1200px)': {
    flexDirection: 'row',
    display: 'flex',
    justifyContent: 'flex-start',
    width: '100%',
  },
}))

export const StyledDashboardSideTextContainer = styled(Box)(({ theme: { palette } }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 16,

  '>p': {
    color: palette.grey[700],
    fontSize: 16,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },

  '@media (min-width: 1200px)': {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'flex-end',
  },
}))

export const StyledDisabledTooltipContainer = styled(Box)(({ theme: { palette } }) => ({
  color: palette.grey[700],
  fontSize: 16,
  display: 'flex',
  alignItems: 'center',
  gap: 8,
}))

export const StyledBoxAvailable = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: 16,
  '>div': {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  },

  '@media (min-width: 1200px)': {
    gap: 24,
  },
}))

type StyledStatusProps = TypographyProps & {
  compliance: EvCapabilitycomplianceType
}

export const StyledTypographyStatus = styled(Typography)<StyledStatusProps>(
  ({ theme: { palette }, compliance }) => ({
    padding: '8px 6px',
    boxShadow:
      compliance === 'SafetyAndService'
        ? '0 0 0 1px #5B788F'
        : `0 0 0 1px ${compliance === 'SafetyOnly' ? '#EF7700' : palette.error.main}`,
    borderRadius: 4,
    ':hover': {
      background: palette.common.white,
    },
    // Conditional styling for img
    ...(compliance === 'SafetyAndService' && {
      img: {
        width: 28,
        height: 28,
        background: '#5B788F',
        padding: 7,
        borderRadius: '50%',
      },
    }),
    button: {
      backgroundColor:
        compliance === 'SafetyAndService'
          ? '#5B788F'
          : compliance === 'SafetyOnly'
          ? '#EF7700'
          : palette.error.main,
      ':hover': {
        backgroundColor:
          compliance === 'SafetyAndService'
            ? '#4c677b'
            : compliance === 'SafetyOnly'
            ? '#cb6705'
            : '#811428',
      },
    },
  }),
)

export const StyledStatusButton = styled(Button)(({ theme: { palette } }) => ({
  color: palette.common.white,
  textTransform: 'uppercase',
  borderRadius: 4,
  fontSize: 14,
  padding: '2px 12px',
  marginLeft: 8,
}))

export const AntSwitch = styled(Switch)(({ theme }) => ({
  width: 28,
  height: 16,
  padding: 0,
  display: 'flex',
  '&:active': {
    '& .MuiSwitch-thumb': {
      width: 15,
    },
    '& .MuiSwitch-switchBase.Mui-checked': {
      transform: 'translateX(9px)',
    },
  },
  '& .MuiSwitch-switchBase': {
    padding: 2,
    '&.Mui-checked': {
      transform: 'translateX(12px)',
      color: '#fff',
      '& + .MuiSwitch-track': {
        opacity: 1,
        backgroundColor: theme.palette.grey[600],
      },
    },
  },
  '& .MuiSwitch-thumb': {
    boxShadow: '0 2px 4px 0 rgb(0 35 11 / 20%)',
    width: 12,
    height: 12,
    borderRadius: 6,
    transition: theme.transitions.create(['width'], {
      duration: 200,
    }),
  },
  '& .MuiSwitch-track': {
    borderRadius: 16 / 2,
    opacity: 1,
    backgroundColor:
      theme.palette.mode === 'dark' ? 'rgba(255,255,255,.35)' : 'rgba(0,0,0,.25)',
    boxSizing: 'border-box',
  },
}))

export const LightTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.common.white,
    marginTop: '24px !important',
    color: 'rgba(0, 0, 0, 0.87)',
    boxShadow: theme.shadows[1],
    padding: '12px 16px',
    maxWidth: 380,
    '& .MuiTooltip-arrow': {
      ':before': {
        background: theme.palette.common.white,
        boxShadow: theme.shadows[1],
      },
    },
  },
}))

export const StyledLightTooltipContainer = styled(Box)(({ theme: { palette } }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
  '>p': {
    color: palette.grey[700],
    fontSize: 14,
  },
}))

export const StyledLightTooltipContent = styled(Box)(({ theme: { palette } }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
  '>p': {
    color: palette.grey[700],
    fontSize: 14,
    fontWeight: 'bold',
  },

  img: {
    width: 28,
    height: 28,
  },

  '>div': {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,

    p: {
      wordBreak: 'normal',
    },

    div: {
      display: 'flex',
      alignItems: 'center',
      gap: 6,
      span: {
        fontSize: '16px',
      },
    },
  },
}))
